/**
 * @description
 * Client selection list component for Platform Sync dialog.
 * Displays unsynced platform clients with checkboxes for selection.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 12, 2025
 * @see admin/src/frontend/components/PlatformSync/PlatformSyncDialog.tsx
 */

import React from 'react';
import { Building2, Users, Clock, CheckCircle, Link } from 'lucide-react';
import type { PlatformClient, LinkableClient } from './types';

interface ClientSelectionListProps {
  platformClients: PlatformClient[];
  linkableClients: LinkableClient[];
  selectedClients: Set<string>;
  onSelectionChange: (selected: Set<string>) => void;
  loading: boolean;
}

/**
 * Component for selecting platform clients to sync
 */
export const ClientSelectionList: React.FC<ClientSelectionListProps> = ({
  platformClients,
  linkableClients,
  selectedClients,
  onSelectionChange,
  loading,
}) => {
  const toggleClient = (clientId: string) => {
    const newSelected = new Set(selectedClients);
    if (newSelected.has(clientId)) {
      newSelected.delete(clientId);
    } else {
      newSelected.add(clientId);
    }
    onSelectionChange(newSelected);
  };

  const selectAll = () => {
    onSelectionChange(new Set(platformClients.map((c) => c.id)));
  };

  const deselectAll = () => {
    onSelectionChange(new Set());
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Unsynced Clients */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">
            Platform Clients ({platformClients.length})
          </h3>
          <div className="flex gap-2">
            <button
              onClick={selectAll}
              className="text-sm text-primary hover:underline"
            >
              Select All
            </button>
            <span className="text-muted-foreground">|</span>
            <button
              onClick={deselectAll}
              className="text-sm text-primary hover:underline"
            >
              Deselect All
            </button>
          </div>
        </div>

        {platformClients.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No unsynced clients found</p>
            <p className="text-sm mt-2">All platform clients are already synced</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {platformClients.map((client) => (
              <button
                key={client.id}
                onClick={() => toggleClient(client.id)}
                className={`p-4 rounded-lg border text-left transition-all ${
                  selectedClients.has(client.id)
                    ? 'border-primary bg-primary/10 ring-2 ring-primary/20'
                    : 'border-border hover:border-border/80 hover:bg-accent/50'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="font-medium text-foreground flex items-center gap-2">
                      <Building2 className="h-4 w-4" />
                      {client.name}
                    </div>
                    {client.subdomain && (
                      <div className="text-sm text-muted-foreground mt-1">
                        {client.subdomain}
                      </div>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        {client.userCount} users
                      </span>
                      {client.lastSyncedAt && (
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {new Date(client.lastSyncedAt).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <div
                    className={`h-5 w-5 rounded border-2 flex items-center justify-center ${
                      selectedClients.has(client.id)
                        ? 'border-primary bg-primary'
                        : 'border-input'
                    }`}
                  >
                    {selectedClients.has(client.id) && (
                      <CheckCircle className="h-4 w-4 text-primary-foreground" />
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Linkable Clients */}
      {linkableClients.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4">
            Linkable Clients ({linkableClients.length})
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            These admin clients can be linked to platform clients
          </p>
          <div className="space-y-3">
            {linkableClients.map((item) => (
              <div
                key={item.adminClient.id}
                className="p-4 rounded-lg border border-border bg-accent/50"
              >
                <div className="flex items-center gap-3">
                  <Link className="h-5 w-5 text-muted-foreground" />
                  <div className="flex-1">
                    <div className="font-medium text-foreground">
                      {item.adminClient.name}
                    </div>
                    {item.suggestedPlatformClients.length > 0 && (
                      <div className="text-sm text-muted-foreground mt-1">
                        Suggested:{' '}
                        {item.suggestedPlatformClients.map((c) => c.name).join(', ')}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */